import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { RocketOutlined } from '@ant-design/icons';

const COLORS = {
  primary: '#1a1a2e',
  surface: '#1e1e3a',
  text: '#e2e8f0',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  emerald: '#10b981',
  violet: '#8b5cf6',
  glass: 'rgba(255,255,255,0.04)',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const glassCard = {
  background: COLORS.glass,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 12,
};

const WarmUpPreview = ({ projection = [] }) => {
  const chartOption = useMemo(() => {
    const xData = projection.map((p) => `Day ${p.day}`);
    const yData = projection.map((p) => p.volume);
    const maxVolume = Math.max(...yData, 1);

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: COLORS.surface,
        borderColor: COLORS.glassBorder,
        textStyle: {
          color: COLORS.text,
          fontSize: 12,
        },
        formatter: (params) => {
          const point = params[0];
          return `<div style="padding:4px 0">
            <div style="font-weight:700;margin-bottom:4px">${point.name}</div>
            <div style="display:flex;align-items:center;gap:6px">
              <span style="width:8px;height:8px;border-radius:50%;background:${COLORS.emerald};display:inline-block"></span>
              Volume: <strong>${point.value.toLocaleString()}</strong> emails
            </div>
          </div>`;
        },
      },
      grid: {
        top: 40,
        right: 24,
        bottom: 40,
        left: 60,
        containLabel: false,
      },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: {
          lineStyle: { color: 'rgba(255,255,255,0.1)' },
        },
        axisLabel: {
          color: COLORS.textMuted,
          fontSize: 10,
          interval: Math.max(0, Math.floor(xData.length / 10) - 1),
        },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        name: 'Email Volume',
        nameTextStyle: {
          color: COLORS.textMuted,
          fontSize: 10,
          padding: [0, 0, 8, 0],
        },
        axisLine: { show: false },
        axisLabel: {
          color: COLORS.textMuted,
          fontSize: 10,
          formatter: (val) => {
            if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
            return val;
          },
        },
        splitLine: {
          lineStyle: { color: 'rgba(255,255,255,0.04)', type: 'dashed' },
        },
        max: Math.ceil(maxVolume * 1.15),
      },
      series: [
        {
          name: 'Daily Volume',
          type: 'line',
          data: yData,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          showSymbol: false,
          emphasis: {
            showSymbol: true,
            symbolSize: 10,
          },
          lineStyle: {
            color: COLORS.emerald,
            width: 2.5,
            shadowColor: 'rgba(16,185,129,0.4)',
            shadowBlur: 8,
            shadowOffsetY: 4,
          },
          itemStyle: {
            color: COLORS.emerald,
            borderColor: '#fff',
            borderWidth: 2,
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(16,185,129,0.35)' },
                { offset: 0.5, color: 'rgba(16,185,129,0.12)' },
                { offset: 1, color: 'rgba(16,185,129,0.01)' },
              ],
            },
          },
        },
      ],
    };
  }, [projection]);

  if (projection.length === 0) {
    return (
      <div style={{ ...glassCard, padding: 32, textAlign: 'center' }}>
        <RocketOutlined style={{ fontSize: 28, color: COLORS.textMuted, marginBottom: 8 }} />
        <div style={{ color: COLORS.textSecondary, fontSize: 13 }}>
          No warm-up projection data available
        </div>
        <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 4 }}>
          Configure warm-up settings to see the volume ramp projection
        </div>
      </div>
    );
  }

  const peakVolume = Math.max(...projection.map((p) => p.volume));
  const totalEmails = projection.reduce((sum, p) => sum + p.volume, 0);

  return (
    <div style={{ ...glassCard, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RocketOutlined style={{ color: COLORS.emerald, fontSize: 18 }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Warm-Up Projection</span>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Peak Volume
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.emerald }}>
              {peakVolume.toLocaleString()}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Emails
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.violet }}>
              {totalEmails.toLocaleString()}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Duration
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>
              {projection.length} days
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <ReactECharts
        option={chartOption}
        style={{ height: 280, width: '100%' }}
        opts={{ renderer: 'svg' }}
      />
    </div>
  );
};

export default WarmUpPreview;
